import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { HubSupportTicketMessageThread } from "@/components/HubSupportTicketMessageThread";
import { MrpSkuHoverPeek, useDeferredMrpSkuHoverHints } from "@/components/mrp/MrpSkuHoverPeek";
import { QueueItemPresenceAvatars } from "@/components/QueueItemPresenceAvatars";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Label } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { useAuth } from "@/components/AuthProvider";
import { useHubToast } from "@/components/ui/HubToast";
import { ApiError } from "@/lib/api";
import {
  DOCUMENT_REQUEST_ASSIGNEES_QUERY_KEY,
  DOCUMENT_REQUEST_QUERY_KEY,
  documentRequestKindLabel,
  documentRequestMessagesAsHubThread,
  documentRequestPriorityLabel,
  documentRequestStatusLabel,
  documentRequestSummary,
  fetchDocumentRequest,
  fetchDocumentRequestAssignees,
  fetchDocumentRequests,
  patchDocumentRequest,
  postDocumentRequestMessage,
  type DocumentRequestStatus,
} from "@/lib/documentRequest";
import { useIntegratorMe } from "@/lib/me-query";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";
import { useQueueItemPresence } from "@/lib/useQueueItemPresence";
import { cn, formatTimestamp } from "@/lib/utils";
import {
  documentRequestAssigneePatchMessage,
  documentRequestUserIdsMatch,
} from "./documentRequestAssigneePatch";

const OPEN_STATUSES: DocumentRequestStatus[] = ["pending", "investigating"];

const DOCUMENT_REQUESTS_QUEUE_PATHNAME = "/documents/requests";

const PAGE_HELP =
  "Staff requests for controlled document access or missing documents on a part number. Document Control administrators grant access by sharing the document, then mark the request granted. For missing documents, upload in the workbench and mark fulfilled.";

function statusTone(status: DocumentRequestStatus): "warning" | "neutral" | "success" | "danger" {
  if (status === "pending") return "warning";
  if (status === "investigating") return "neutral";
  if (status === "granted" || status === "fulfilled") return "success";
  if (status === "denied") return "danger";
  return "neutral";
}

export function DocumentRequestsPage(): JSX.Element {
  const canAdmin = useHubModuleAdmin("documents");
  const meQuery = useIntegratorMe();
  const { session } = useAuth();
  const currentUserId =
    meQuery.data?.user.id?.trim()
    || session?.user.id?.trim()
    || "";
  const { showToast } = useHubToast();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("request")?.trim() ?? "";
  const [statusFilter, setStatusFilter] = useState<"open" | "all">("open");
  const [pendingFeedbackOnly, setPendingFeedbackOnly] = useState(false);
  const [resolutionNote, setResolutionNote] = useState("");
  const [replyBody, setReplyBody] = useState("");

  const listQuery = useQuery({
    queryKey: [...DOCUMENT_REQUEST_QUERY_KEY, "staff", "list", statusFilter, pendingFeedbackOnly],
    queryFn: () =>
      fetchDocumentRequests({
        status: statusFilter === "open" ? OPEN_STATUSES.join(",") : undefined,
        pendingFeedback: pendingFeedbackOnly,
        limit: 100,
      }),
    enabled: canAdmin,
  });

  const detailQuery = useQuery({
    queryKey: [...DOCUMENT_REQUEST_QUERY_KEY, "staff", "detail", selectedId],
    queryFn: () => fetchDocumentRequest(selectedId),
    enabled: canAdmin && selectedId !== "",
  });

  const assigneeOptionsQuery = useQuery({
    queryKey: DOCUMENT_REQUEST_ASSIGNEES_QUERY_KEY,
    queryFn: () => fetchDocumentRequestAssignees(),
    enabled: canAdmin && selectedId !== "",
  });

  useEffect(() => {
    if (detailQuery.data?.request) {
      setResolutionNote(detailQuery.data.request.resolution_note ?? "");
    }
  }, [detailQuery.data?.request?.id, detailQuery.data?.request?.resolution_note]);

  const patchMutation = useMutation({
    mutationFn: (body: Parameters<typeof patchDocumentRequest>[1]) => patchDocumentRequest(selectedId, body),
    onSuccess: (_data, body) => {
      void queryClient.invalidateQueries({ queryKey: DOCUMENT_REQUEST_QUERY_KEY });
      const assigneeMessage = documentRequestAssigneePatchMessage(body.assignedToUserId, currentUserId);
      if (assigneeMessage != null) {
        showToast({ tone: "success", message: assigneeMessage });
        return;
      }
      showToast({ tone: "success", message: "Request updated." });
    },
    onError: (error: Error, body) => {
      if (body.assignedToUserId !== undefined) {
        showToast({ tone: "error", message: error.message });
        return;
      }
      showToast({ tone: "error", message: error.message });
    },
  });

  function patchRequest(body: Parameters<typeof patchDocumentRequest>[1]): void {
    if (selectedId === "") {
      return;
    }
    patchMutation.mutate(body);
  }

  const replyMutation = useMutation({
    mutationFn: (body: string) => postDocumentRequestMessage(selectedId, body),
    onSuccess: () => {
      setReplyBody("");
      void queryClient.invalidateQueries({ queryKey: [...DOCUMENT_REQUEST_QUERY_KEY, "staff", "detail", selectedId] });
    },
    onError: (error: Error) => showToast({ tone: "error", message: error.message }),
  });

  const { viewersForEntity } = useQueueItemPresence({
    pathname: DOCUMENT_REQUESTS_QUEUE_PATHNAME,
    enabled: canAdmin,
  });

  if (!canAdmin) {
    return (
      <div className="mx-auto max-w-3xl py-8">
        <p className="text-sm text-slate-600">
          Document Control administrator access is required to review document requests.
        </p>
      </div>
    );
  }

  const rows = listQuery.data?.rows ?? [];

  const queueSkus = useMemo(() => rows.map((row) => row.sku), [rows]);
  const { skuHoverHints, hintsLoading, enableHintsFetch } = useDeferredMrpSkuHoverHints(
    queueSkus,
    "document-requests",
  );

  const request = detailQuery.data?.request;
  const messages = detailQuery.data?.messages ?? [];
  const isOpen =
    request != null && (request.status === "pending" || request.status === "investigating");
  const isAssignedToCurrentUser =
    currentUserId !== ""
    && request?.assigned_to_user_id != null
    && documentRequestUserIdsMatch(request.assigned_to_user_id, currentUserId);

  const assigneeOptions = useMemo(() => {
    const users = assigneeOptionsQuery.data?.users ?? [];
    const options = users.map((user) => ({
      userId: user.id,
      label: user.display_name?.trim() ? user.display_name : user.id,
    }));
    const currentAssigneeId = request?.assigned_to_user_id?.trim() ?? "";
    if (
      currentAssigneeId !== ""
      && !options.some((option) => option.userId === currentAssigneeId)
      && request?.assigned_to_display_name
    ) {
      options.unshift({
        userId: currentAssigneeId,
        label: request.assigned_to_display_name,
      });
    }
    return options;
  }, [
    assigneeOptionsQuery.data?.users,
    request?.assigned_to_display_name,
    request?.assigned_to_user_id,
  ]);

  return (
    <div className="space-y-4">
      <PageTitleWithHelp hoverHint={PAGE_HELP}>Document requests</PageTitleWithHelp>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,22rem)_1fr]">
        <Card className="h-fit">
          <CardHeader className="space-y-3">
            <CardTitle>Queue</CardTitle>
            <div className="flex flex-wrap gap-2 text-sm">
              <button
                type="button"
                className={cn(
                  "rounded-full px-3 py-1",
                  statusFilter === "open" ? "bg-brand-100 text-brand-900" : "bg-slate-100 text-slate-700",
                )}
                onClick={() => setStatusFilter("open")}
              >
                Open
              </button>
              <button
                type="button"
                className={cn(
                  "rounded-full px-3 py-1",
                  statusFilter === "all" ? "bg-brand-100 text-brand-900" : "bg-slate-100 text-slate-700",
                )}
                onClick={() => setStatusFilter("all")}
              >
                All
              </button>
              <label className="flex items-center gap-2 text-slate-600">
                <input
                  type="checkbox"
                  checked={pendingFeedbackOnly}
                  onChange={(event) => setPendingFeedbackOnly(event.target.checked)}
                />
                Pending feedback
              </label>
            </div>
          </CardHeader>
          <CardBody className="max-h-[32rem] overflow-y-auto p-0 sm:p-0">
            {listQuery.isLoading ? (
              <p className="px-4 py-3 text-sm text-slate-500">Loading…</p>
            ) : listQuery.isError ? (
              <p className="px-4 py-3 text-sm text-red-600">{(listQuery.error as Error).message}</p>
            ) : rows.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-slate-600">No requests match this filter.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <li key={row.id}>
                    <button
                      type="button"
                      className={cn(
                        "flex w-full flex-col gap-1 px-4 py-3 text-left hover:bg-slate-50",
                        row.id === selectedId && "bg-brand-50/80",
                      )}
                      onClick={() => {
                        setSearchParams((previous) => {
                          const merged = new URLSearchParams(previous);
                          merged.set("request", row.id);
                          return merged;
                        });
                      }}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-slate-900">#{row.id}</span>
                        <MrpSkuHoverPeek
                          sku={row.sku}
                          hint={skuHoverHints?.[row.sku.trim()]}
                          hintsLoading={hintsLoading}
                          onHoverIntent={enableHintsFetch}
                        />
                        <Badge tone={statusTone(row.status)}>{documentRequestStatusLabel(row.status)}</Badge>
                        {row.pending_feedback ? <Badge tone="warning">Feedback</Badge> : null}
                        <Badge tone={row.assigned_to_display_name ? "neutral" : "warning"}>
                          {row.assigned_to_display_name?.trim() || "Unassigned"}
                        </Badge>
                        <QueueItemPresenceAvatars
                          people={viewersForEntity(row.id)}
                          className="ml-auto shrink-0"
                        />
                      </div>
                      <span className="text-xs text-slate-500">{documentRequestKindLabel(row.kind)}</span>
                      <span className="line-clamp-2 text-sm text-slate-700">{documentRequestSummary(row)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle>
              {request ? (
                <span className="inline-flex flex-wrap items-center gap-x-1">
                  #{request.id} ·{" "}
                  <MrpSkuHoverPeek
                    sku={request.sku}
                    hint={skuHoverHints?.[request.sku.trim()]}
                    hintsLoading={hintsLoading}
                    onHoverIntent={enableHintsFetch}
                    tab="documents"
                  />
                  {" · "}
                  {documentRequestSummary(request)}
                </span>
              ) : (
                "Select a request"
              )}
            </CardTitle>
            {request != null ? (
              isOpen ? (
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    id="document-request-assignee"
                    aria-label="Assignee"
                    className="h-8 max-w-[11rem] rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    value={request.assigned_to_user_id ?? ""}
                    disabled={patchMutation.isPending || assigneeOptionsQuery.isLoading}
                    onChange={(event) => {
                      const value = event.target.value;
                      patchRequest({
                        assignedToUserId: value === "" ? null : value,
                      });
                    }}
                  >
                    <option value="">Unassigned</option>
                    {assigneeOptions.map((option) => (
                      <option key={option.userId} value={option.userId}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={
                      patchMutation.isPending
                      || (meQuery.isLoading && currentUserId === "")
                      || currentUserId === ""
                      || isAssignedToCurrentUser
                    }
                    onClick={() => patchRequest({ assignedToUserId: currentUserId })}
                  >
                    Take over
                  </Button>
                </div>
              ) : (
                <span className="text-sm text-slate-600">
                  {request.assigned_to_display_name?.trim() || "Unassigned"}
                </span>
              )
            ) : null}
          </CardHeader>
          <CardBody className="space-y-4">
            {!selectedId ? (
              <p className="text-sm text-slate-600">Select a request from the queue.</p>
            ) : detailQuery.isLoading && !request ? (
              <p className="text-sm text-slate-500">Loading request…</p>
            ) : detailQuery.isError ? (
              <p className="text-sm text-red-600">
                {detailQuery.error instanceof ApiError ? detailQuery.error.message : "Request not found."}
              </p>
            ) : request ? (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={statusTone(request.status)}>{documentRequestStatusLabel(request.status)}</Badge>
                  <span className="text-sm text-slate-600">{documentRequestKindLabel(request.kind)}</span>
                  <MrpSkuHoverPeek
                    sku={request.sku}
                    hint={skuHoverHints?.[request.sku.trim()]}
                    hintsLoading={hintsLoading}
                    onHoverIntent={enableHintsFetch}
                    tab="documents"
                    className="text-sm"
                  />
                  <span className="text-xs text-slate-500">
                    {request.requester_display_name ?? request.requester_email} ·{" "}
                    {formatTimestamp(request.created_at)}
                  </span>
                </div>
                {request.document_record_id ? (
                  <p className="text-sm text-slate-700">
                    Document:{" "}
                    <Link to="/documents/workbench" className="text-sky-700 hover:text-sky-900">
                      {request.document_title ?? request.document_record_id}
                    </Link>
                  </p>
                ) : null}
                {request.kind === "new_document" ? (
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-700">
                    {request.priority ? (
                      <span>
                        <span className="font-medium text-slate-600">Priority:</span>{" "}
                        {documentRequestPriorityLabel(request.priority)}
                      </span>
                    ) : null}
                    {request.requested_by_date ? (
                      <span>
                        <span className="font-medium text-slate-600">Request by:</span>{" "}
                        {request.requested_by_date}
                      </span>
                    ) : null}
                    {request.document_kind_label ? (
                      <span>
                        <span className="font-medium text-slate-600">Document type:</span>{" "}
                        {request.document_kind_label}
                      </span>
                    ) : null}
                  </div>
                ) : null}
                <pre className="whitespace-pre-wrap rounded-md border border-slate-100 bg-slate-50 p-3 text-sm text-slate-800">
                  {request.body}
                </pre>
                <div>
                  <Label htmlFor="document-request-resolution">Resolution note</Label>
                  <textarea
                    id="document-request-resolution"
                    rows={3}
                    value={resolutionNote}
                    onChange={(event) => setResolutionNote(event.target.value)}
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    placeholder="Visible to the requester when you close the request…"
                  />
                </div>
                {isOpen ? (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={patchMutation.isPending}
                      onClick={() => {
                        void patchMutation.mutateAsync({ status: "investigating", resolutionNote });
                      }}
                    >
                      Investigating
                    </Button>
                    {request.kind === "access" ? (
                      <Button
                        type="button"
                        disabled={patchMutation.isPending}
                        onClick={() => {
                          void patchMutation.mutateAsync({ status: "granted", resolutionNote, pendingFeedback: false });
                        }}
                      >
                        Mark access granted
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        disabled={patchMutation.isPending}
                        onClick={() => {
                          void patchMutation.mutateAsync({ status: "fulfilled", resolutionNote, pendingFeedback: false });
                        }}
                      >
                        Mark fulfilled
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={patchMutation.isPending}
                      onClick={() => {
                        void patchMutation.mutateAsync({
                          pendingFeedback: true,
                          resolutionNote,
                        });
                      }}
                    >
                      Pending feedback
                    </Button>
                    <Button
                      type="button"
                      variant="danger"
                      disabled={patchMutation.isPending}
                      onClick={() => {
                        if (resolutionNote.trim() === "") {
                          showToast({ tone: "error", message: "Add a resolution note before denying." });
                          return;
                        }
                        void patchMutation.mutateAsync({ status: "denied", resolutionNote, pendingFeedback: false });
                      }}
                    >
                      Deny
                    </Button>
                  </div>
                ) : null}
                <HubSupportTicketMessageThread
                  messages={documentRequestMessagesAsHubThread(messages)}
                  draft={replyBody}
                  onDraftChange={setReplyBody}
                  submitting={replyMutation.isPending}
                  canReply={isOpen}
                  onSubmit={() => {
                    const trimmed = replyBody.trim();
                    if (trimmed === "") return;
                    void replyMutation.mutateAsync(trimmed);
                  }}
                />
              </>
            ) : (
              <p className="text-sm text-slate-600">Request not found.</p>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
